import useStateValue from "frontend/state/value";
import style from "./share-canvas-modal-new.module.css";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { CloseIcon } from "frontend/ui-components/svg-shapes";
import InviteToBoardByLink from "./invite-to-board-by-link";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import { CheckoutSource } from "frontend/billing/checkout";
import { Board, BoardPermission, PlanFeatureName, Permission, TipName } from "shared/datamodel/schemas";
import DropdownPicker from "frontend/ui-components/picker/dropdown-picker";
import { useInvite } from "frontend/hooks/use-invite";
import { editBoard } from "frontend/services/boardsService";
import usePlanFeatureValue from "frontend/hooks/use-plan-features";
import AccessToast from "frontend/ui-components/access-toast";
import useAbility from "frontend/hooks/use-ability";
import { InstanceType } from "shared/datamodel/schemas/ability";
import NewPaidSeatsModal from "../new-paid-seats-modal";
import SendInviteToast from "frontend/ui-components/toast/send-invite-toast";
import { getPathPrefix } from "../../utils/getPathPrefix";
import ManageCollaborationMembers from "../collaboration/manage-collaboration-members";
import { Team } from "shared/datamodel/schemas/team";
import { Project } from "shared/datamodel/schemas/project";
import { useProjectsCache } from "frontend/hooks/caching/use-projects-cache";
import { useTeamsCache } from "frontend/hooks/caching/use-teams-cache";
import AppLoader from "frontend/loader/app-loader";
import StyledButton from "frontend/ui-components/styled-button";
import useInviteNew from "frontend/hooks/use-invite-new";
import { InviteResponse } from "utils/invite-utils";
import { renderResponseStrip } from "frontend/ui-components/invite-emails";
import FormBubblesField from "frontend/ui-components/form-fields/form-bubbles-field";
import BulkPermissionRound from "../collaboration/team/bulk-permission-round";
import PeopleWithAccessToCanvas from "./people-with-access-to-canvas";
import WalkthroughTipPresenter from "frontend/walkthrough-tips/walkthrough-tip-presenter";
import { WalkthroughTipType } from "frontend/walkthrough-tips/walkthrough-tip-model";
import AccountMembers from "../collaboration/account-members";
import ChooseInviteTypeModal from "../choose-invite-type-modal";
import { useBoardValue } from "frontend/state/board-state";
import { isBasicPlan } from "shared/util/billing";

enum AccessType {
  Private = "Private",
  Public = "Public",
}

export default function ShareCanvasModalNew({
  board,
  setShowInviteModal,
  setShowUpgradeModal,
  passwordChecked,
  setPasswordChecked,
}: {
  board: Board;
  setShowInviteModal: (show: boolean) => void;
  setShowUpgradeModal: (checkoutSource: null | CheckoutSource) => void;
  passwordChecked: boolean;
  setPasswordChecked: (checked: boolean) => void;
}) {
  const [{ user }, dispatch] = useStateValue();
  const [, dispatchBoard] = useBoardValue();
  const isAnonymousEnabled = usePlanFeatureValue(PlanFeatureName.ANONYMOUS_ENABLED);
  const isTouchAccount = user?.planInfo?.source === "touch";
  const { inviteUsersResponse } = useInvite();

  const [selectedAccess, setSelectedAccess] = useState<AccessType>(
    board && board?.permission !== BoardPermission.private ? AccessType.Public : AccessType.Private
  );
  const [shouldRenderNewPaidSeatsModal, setShouldRenderNewPaidSeatsModal] = useState<boolean>(false);
  const [changingPermissionMessage, setChangingPermissionMessage] = useState<string | null>(null);
  const [inviteeName, setInviteeName] = useState<string | null>(null);
  const [showManageInstanceMembersModal, setShowManageInstanceMembersModal] = useState<boolean>(false);
  const [parentInstance, setParentInstance] = useState<Team | Project | null>(null);
  const [parentInstanceType, setParentInstanceType] = useState<InstanceType>(InstanceType.Account);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { canEditInstance, canInviteToInstance } = useAbility();
  const userPermissionString = canEditInstance(user, board, InstanceType.Board) ? "editor" : "viewer";
  const canInvite = canInviteToInstance(user, board, InstanceType.Board) ? "can-invite" : "cannot-invite";
  const [bulkPermission, setBulkPermission] = useState<Permission>(Permission.editor);
  const inviteByEmailRef = useRef<HTMLInputElement>(null);
  const isFreePlan = !user || isBasicPlan(user?.planInfo?.plan_id);

  const {
    users,
    failed,
    addedSeats,
    changeEmails,
    emails,
    highlightInvalidEmails,
    loading,
    unsubscribed,
    shouldRenderUnsubscribedModal,
    setShouldRenderUnsubscribedModal,
    addAsRestricted: _addAsRestricted,
    setAddAsRestricted,
    handleInviteFlow,
  } = useInviteNew({
    bulkPermission,
    instance: board,
    instanceType: InstanceType.Board,
    source: "share-canvas-modal",
    isFreePlan: isFreePlan || false,
  });
  const invalidCount = emails.filter((v: any) => !v.isValid).length;
  const { getProjects } = useProjectsCache();
  const { getTeams } = useTeamsCache();

  useEffect(() => {
    if (selectedAccess === AccessType.Private) {
      tracking.trackAnalyticsEvent("invite-modal-opened", {
        category: consts.TRACKING_CATEGORY.INVITE_ACTION,
        source: "share-canvas-modal",
      });
    }
  }, [selectedAccess]);

  useEffect(() => {
    if (inviteUsersResponse && inviteUsersResponse.addedSeats > 0) {
      !isTouchAccount && setShouldRenderNewPaidSeatsModal(true);
    }
  }, [inviteUsersResponse, isTouchAccount]);

  useEffect(() => {
    let timeOut: NodeJS.Timeout;
    if (changingPermissionMessage) {
      timeOut = setTimeout(() => {
        setChangingPermissionMessage(null);
      }, 4000);
    }
    return () => timeOut && clearTimeout(timeOut);
  }, [changingPermissionMessage]);

  useEffect(() => {
    let timeOut: NodeJS.Timeout;
    if (inviteeName) {
      timeOut = setTimeout(() => {
        setInviteeName(null);
      }, 6000);
    }
    return () => timeOut && clearTimeout(timeOut);
  }, [inviteeName]);

  useEffect(() => {
    async function loadInstance() {
      setIsLoading(true);
      if (board.projectDocumentId) {
        const projects = await getProjects([board.projectDocumentId]);
        if (projects.length > 0) {
          setParentInstance(projects[0] as Project);
          setParentInstanceType(InstanceType.Project);
        }
      } else if (board.teamDocumentId) {
        const teams = await getTeams([board.teamDocumentId]);
        if (teams.length > 0) {
          setParentInstance(teams[0] as Team);
          setParentInstanceType(InstanceType.Team);
        }
      }
      setIsLoading(false);
    }
    loadInstance();
  }, [board.projectDocumentId, board.teamDocumentId, getProjects, getTeams]);

  function onDismiss() {
    setShowInviteModal(false);
  }

  function renderAccessOption({ option }: { option: AccessType }) {
    const shouldUpgrade = option === AccessType.Public && !isAnonymousEnabled;

    const element = (
      <div className={classNames(style.option, { [style.disabled]: shouldUpgrade })}>
        <span className={style.optionAccess}>{`${option} Access`}</span>
        {shouldUpgrade && (
          <button
            className={style.upgradeButton}
            onClick={(e) => {
              e.preventDefault();
              tracking.trackEvent(
                consts.TRACKING_CATEGORY.ADMIN_ACTION,
                "available-in-pro-clicked",
                "upgrade for public link",
                userPermissionString,
                canInvite
              );
              onDismiss();
              setShowUpgradeModal({ name: "public-boards", value: "public-boards" });
            }}
          >
            Available in Pro
          </button>
        )}
      </div>
    );
    return {
      element,
      enabled: !shouldUpgrade,
      customStyle: { padding: "0 16px", fontSize: 12, fontWeight: 300 },
      customIcon:
        option === AccessType.Private
          ? getPathPrefix("/images/lock-canvas-black.svg")
          : getPathPrefix("/images/public.svg"),
      handlesSelectedState: true,
    };
  }
  function handleModifyPermission({
    newPermission,
    password,
  }: {
    newPermission: BoardPermission;
    password?: string | null;
  }) {
    setChangingPermissionMessage("Access updated");
    const newBoard = { ...board, permission: newPermission, password };
    dispatchBoard({ type: "set_board", payload: newBoard });
    editBoard(dispatch, newBoard, board).catch(() => {
      dispatchBoard({ type: "set_board", payload: board });
    });
    tracking.trackEvent(
      consts.TRACKING_CATEGORY.ADMIN_ACTION,
      "change_board_permission",
      newPermission,
      userPermissionString,
      canInvite
    );
  }

  function onChangePermission(newPermission: BoardPermission) {
    let password;
    if (newPermission === BoardPermission.private) {
      password = null;
      setPasswordChecked(false);
    }
    handleModifyPermission({ newPermission, password });
  }

  function chooseSelectedAccessType(accessType: AccessType) {
    if (accessType === AccessType.Private) {
      onChangePermission(BoardPermission.private);
    }
  }

  function changeSelectedAccessType(accessType: AccessType) {
    setSelectedAccess(accessType);
    chooseSelectedAccessType(accessType);
  }

  function renderPermissionPicker() {
    return board?.isOwner ? (
      <div className={style.accessPermissionPicker}>
        <DropdownPicker
          options={[AccessType.Private, AccessType.Public]}
          titles={(v) => `${v} Access`}
          optionRenderer={renderAccessOption}
          onChange={(v) => {
            changeSelectedAccessType(v);
            if (v === AccessType.Private) {
              onChangePermission(BoardPermission.private);
            } else {
              const newPermission =
                board.permission === BoardPermission.publicReadOnly
                  ? BoardPermission.publicReadOnly
                  : BoardPermission.public;
              onChangePermission(newPermission);
            }
          }}
          isSelected={(v) => selectedAccess === v}
          enabled={true}
          pickerCustomStyle={{ border: "none", padding: 0, height: "auto", fontSize: 14, fontWeight: 500 }}
          optionsContainerStyle={{ width: "fit-content" }}
        />
      </div>
    ) : (
      <div className={style.option} style={{ marginLeft: 25 }}>
        <img
          src={
            selectedAccess === AccessType.Private
              ? getPathPrefix("/images/lock-canvas-black.svg")
              : getPathPrefix("/images/public.svg")
          }
          alt={`${selectedAccess} access icon`}
          width={24}
          height={24}
        />
        <span className={style.selectedOption}>{`${selectedAccess} Access`}</span>
      </div>
    );
  }

  function renderInviteByEmail() {
    return (
      <>
        <div className={style.shareOptionContent}>
          <div className={style.inviteByEmailContainer} ref={inviteByEmailRef}>
            {renderResponseStrip({ usersCount: users.length, failed, addedSeats } as InviteResponse)}
            <div className={style.inputContainer}>
              <div className={style.inputField}>
                <FormBubblesField
                  id={"emails"}
                  onChange={(e) => changeEmails(e, "invite-to-board")}
                  value={emails}
                  placeholder="Enter email addresses"
                  highlightInvalid={highlightInvalidEmails}
                  autoFocus={true}
                  customStyle={{ borderRadius: 6, paddingLeft: 10 }}
                  rounded
                />
              </div>
              <BulkPermissionRound
                usersPermission={bulkPermission}
                setUsersPermission={setBulkPermission}
                showText={false}
                customStyle={{ padding: 0 }}
                dropDownCustomStyle={{ height: 36, fontSize: 14 }}
              />
            </div>
            {invalidCount > 0 && (
              <div className={style.dynamicErrorMessage}>
                <img src={getPathPrefix("/images/error-info-icon.svg")} alt="" />
                <span>
                  {`you have entered ${invalidCount} invalid email addresses. Please check them from typos and enter new ones.`}
                </span>
              </div>
            )}
            <WalkthroughTipPresenter
              tipName={TipName.walkthroughInviteInShareCanvas}
              attachToRef={inviteByEmailRef}
              side="right"
              showTipOnModal={true}
              type={WalkthroughTipType.HomePage}
            />
          </div>
        </div>
      </>
    );
  }

  function renderInviteBy() {
    if (board) {
      return (
        <div>
          {selectedAccess === AccessType.Private ? (
            board.isOwner ? (
              renderInviteByEmail()
            ) : null
          ) : (
            <InviteToBoardByLink
              board={board}
              onChangePermission={onChangePermission}
              onPasswordSet={onPasswordSet}
              passwordChecked={passwordChecked}
              setPasswordChecked={setPasswordChecked}
            />
          )}
        </div>
      );
    }
  }

  function onPasswordSet(password?: string | null) {
    handleModifyPermission({ newPermission: board.permission, password });
  }

  function openMembersModal() {
    setShowManageInstanceMembersModal(true);
  }

  function renderChooseAccessType() {
    return showManageInstanceMembersModal && parentInstance ? (
      (parentInstance as Team)?.accountTeam ? (
        <AccountMembers
          title={parentInstance.name}
          onDismiss={() => setShowManageInstanceMembersModal(false)}
          onEditSeats={() => setShowManageInstanceMembersModal(false)}
        />
      ) : (
        <ManageCollaborationMembers
          instanceId={parentInstance.documentId}
          type={parentInstanceType}
          onDismiss={() => setShowManageInstanceMembersModal(false)}
        />
      )
    ) : (
      <>
        <div className={style.chooseAccessTypeContainer}>
          <div className={style.closeIcon}>
            <CloseIcon
              onClick={() => onDismiss()}
              color={"#848199"}
              style={{
                zIndex: 9999,
              }}
            />
          </div>
          <div className={classNames(style.content, style.chooseAccessContent)}>
            <div className={classNames(style.header)}>
              <p className={style.title}>Share Canvas</p>
              {renderPermissionPicker()}
              {renderInviteBy()}
            </div>
            <div className={style.divider} />
            <div className={classNames(style.membersHeader)}>
              <p className={style.collaboratorsSubtitle}>{`People with access`}</p>
            </div>
            <PeopleWithAccessToCanvas
              board={board}
              parentInstance={parentInstance}
              openMembersModal={openMembersModal}
            />
          </div>
          <div className={style.divider} />

          <div className={style.sendInvite}>
            <StyledButton
              title={"Send Invite"}
              enabled={emails.length > 0 && invalidCount === 0}
              loading={loading}
              loadingTitle="Inviting..."
              onClick={handleInviteFlow}
              disabledColor="#DEE1EB"
              customStyle={{
                minWidth: "137px",
                width: "fit-content",
                height: "33px",
                borderRadius: 6,
                color: "#FFF",
                textAlign: "center",
                fontFamily: "Poppins",
                fontSize: 14,
                fontWeight: 400,
              }}
            />
          </div>
        </div>
        {inviteeName && <SendInviteToast inviteeName={inviteeName} />}
        {changingPermissionMessage && <AccessToast text={changingPermissionMessage} />}
      </>
    );
  }

  function renderNewPaidSeatsModal() {
    return (
      <NewPaidSeatsModal
        onDismiss={() => setShouldRenderNewPaidSeatsModal(false)}
        addedSeats={inviteUsersResponse?.addedSeats ?? 0}
        invitedCount={inviteUsersResponse?.usersCount ?? 0}
        failedEmails={inviteUsersResponse?.failed ?? []}
      />
    );
  }

  function renderModal() {
    if (isLoading) {
      return <AppLoader />;
    }
    if (shouldRenderUnsubscribedModal) {
      return (
        <ChooseInviteTypeModal
          unsubscribed={unsubscribed}
          setShouldRenderUnsubscribedModal={setShouldRenderUnsubscribedModal}
          setAddAsRestricted={setAddAsRestricted}
          onDismiss={onDismiss}
        />
      );
    }
    if (shouldRenderNewPaidSeatsModal) {
      return renderNewPaidSeatsModal();
    }
    return renderChooseAccessType();
  }

  return renderModal();
}
